import { Component, ElementRef, Inject, OnInit, Optional, QueryList, TemplateRef, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, Subject, Subscription, auditTime, catchError, map, merge, of, startWith, switchMap, take, takeUntil, timer } from 'rxjs';
import { EntitiesFacade, MetaDataProviders } from 'src/app/abstraction/entities.facade';
import { NomenclatorFacade } from 'src/app/abstraction/nomenclator.facade';
import { MedicalLicenseTypes } from 'src/app/core/enums/medicalLicenseTypes';
import { GeneralService } from 'src/app/core/services/general.service';
import { LocationsQPS, PatientsService } from 'src/app/core/services/patients.service';
import { NationsQPS, PatchProviderBody, ProvidersQPS, ProvidersService } from 'src/app/core/services/providers.service';
import { CustomValidators } from 'src/app/core/validators/custom-validators';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { Address } from 'src/app/shared/models/address';
import { AddressData, Location } from 'src/app/shared/models/location';
import { MedicalLicense } from 'src/app/shared/models/medicalLicense';
import { Nationality } from 'src/app/shared/models/nationality';
import { Provider } from 'src/app/shared/models/provider';
import { Specialty } from 'src/app/shared/models/specialty';
import Swal from 'sweetalert2';
import { AuthenticationService } from 'src/app/core/authentication.service';
import { ProviderLocation } from '../../../../../core/services/providers.service';
import { PROVIDER_STATUS } from 'src/app/core/enums/providerStatus';
import { AlertService } from 'src/app/core/services/alert.service';
import { EntitiesFiles } from 'src/app/core/enums/entitiesFiles';
import { File } from 'src/app/shared/models/fileUrl';
import { FileUtilitiesService } from 'src/app/core/services/fileUtilities.service';
import { DialogImageDetailComponent } from 'src/app/shared/components/dialog-image-detail/dialog-image-detail.component';
import { TypeDocument } from 'src/app/shared/models/typeDocument';
import { EvolutionsFacade } from 'src/app/abstraction/evolutions.facade';
import { ROUTES, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { TextInputComponent } from 'src/app/shared/components/forms/text-input/text-input.component';
import { NumberInputComponent } from 'src/app/shared/components/forms/number-input/number-input.component';
import { DatePickerComponent } from 'src/app/shared/components/forms/date-picker/date-picker.component';
import { SelectSearchingDynamicInputComponent } from 'src/app/shared/components/forms/select-searching-dynamic-input/select-searching-dynamic-input.component';
import { SelectSearchingInputComponent } from 'src/app/shared/components/forms/select-searching-input/select-searching-input.component';
import { SelectInputComponent } from 'src/app/shared/components/forms/select-input/select-input.component';
import { AddressInputComponent } from 'src/app/shared/components/forms/address-input/address-input.component';
import { SpecialtyProfessionalType } from 'src/app/shared/models/specialtyProfessionalType';

export interface ProviderToInput {
    city: string,
    province: string
}
@Component({
    selector: 'app-providers-management',
    templateUrl: './providers-management.component.html',
    styleUrls: ['./providers-management.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ProvidersManagementComponent implements OnInit {
    /**
     * Habilita acciones de matrícula médica (SISA, cargar archivos) según reglas de id y value
     * - Si el id es distinto al default, habilita
     * - Si el id es default pero el value tiene contenido (no vacío, no '0'), habilita
     * - Si el value es vacío, null, undefined o '0', deshabilita
     */
    /**
     * Evalúa si las acciones de la matrícula médica están habilitadas, usando los índices para obtener el control exacto.
     */
    isMedicalLicenseActionsEnabled(specialtyIndex: number, licenseIndex: number): boolean {
        const medicalLicense = this.getMedicalLicensesFormArray(specialtyIndex).at(licenseIndex);
        if (!medicalLicense) return false;
        const id = medicalLicense.get(this.MEDICAL_LICENSE_ID)?.value;
        const value = medicalLicense.get(this.MEDICAL_LICENSE_VALUE)?.value;

        if (id !== this.DEFAULT_LICENSE_ID && (!!value || Number(value) === 0)) return true;
        if (id === this.DEFAULT_LICENSE_ID && value != null && value !== '' && value !== '0') return true;
        return false;
    }
    @ViewChild('myPond', { static: false }) myPond: any;
    @ViewChild('addOrModifyProviderTemplate', { static: true }) addOrModifyProviderTemplate: TemplateRef<any>;
    @ViewChild('licenseInput') licenseInput: ElementRef<HTMLInputElement>;

    @ViewChildren(TextInputComponent)
    private _textInputs!: QueryList<TextInputComponent>;
    @ViewChildren(NumberInputComponent)
    private _numberInputs!: QueryList<NumberInputComponent>;
    @ViewChildren(DatePickerComponent)
    private _dateInputs!: QueryList<DatePickerComponent>;
    @ViewChildren(SelectSearchingDynamicInputComponent)
    private _selectSearchingDinamicInputs!: QueryList<SelectSearchingDynamicInputComponent>;
    @ViewChildren(SelectSearchingInputComponent)
    private _selectSearchingInputs!: QueryList<SelectSearchingInputComponent>;
    @ViewChildren(SelectInputComponent)
    private _selectInputs!: QueryList<SelectSearchingInputComponent>;
    @ViewChildren(AddressInputComponent)
    private _addressInputs!: QueryList<AddressInputComponent>;

    public readonly NAME = 'name';
    public readonly SURNAME = 'surname';
    public readonly ACTIVE = 'active';
    public readonly CUIT = 'cuit';
    public readonly EMAIL = 'email';
    public readonly DOCUMENT_MEDIA_FRONT = 'documentMediaFront';
    public readonly DOCUMENT_MEDIA_BACK = 'documentMediaBack';
    public readonly PROFILE_MEDIA = 'profile';
    public readonly MOBILE_PHONENUMBER = 'mobilePhoneNumber';
    public readonly BIRTHDATE = 'birthDate';
    public readonly ADDRESS = 'address';
    public readonly ADDRESS_FLAT = 'flat';
    public readonly PROVIDER_LOCATION_LATITUDE = 'latitude';
    public readonly PROVIDER_LOCATION_LONGITUDE = 'longitude';
    public readonly ADDRESS_FLATEXTRA = 'flatExtra';
    public readonly ADDRESS_FLATEXTRA_MAX_LENGTH = 500;
    public readonly ADDRESS_FLOOR = 'floor';
    public readonly ADDRESS_STREET = 'street';
    public readonly ADDRESS_STREETNUMBER = 'streetNumber';
    public readonly ADDRESS_WITHOUT_STREET_NUMBER = 'withoutNumber';
    public readonly ADDRESS_LOCATION = 'location';
    public readonly ADDRESS_LOCATION_SEARCHING = 'locationSearching';
    public readonly NATIONALITY = 'nationality';
    public readonly NATIONALITY_SEARCHING = 'nationalitySearching';
    public readonly SPECIALTIES = 'specialties';
    public readonly SPECIALTIES_ID = 'id';
    public readonly SPECIALTIES_SEARCHING = 'specialtiesSearching';
    public readonly MEDICAL_LICENSES = 'medicalLicenses';
    public readonly SPECIALTY_PROFESSIONAL_TYPE_ID = 'specialtyProfessionalTypeId';
    public readonly MEDICAL_LICENSE_ID = 'id';
    public readonly MEDICAL_LICENSE_VALUE = 'value';
    public readonly MEDICAL_LICENSE_VALIDITY = 'validity';
    public readonly MEDICAL_LICENSE_TYPE = 'type';
    public readonly MEDICAL_LICENSE_MEDIA = 'medicalLicenseMedia'
    public readonly TITLE_PROVIDER = 'Lista de prestadores';
    public readonly EXPIRATION_DATE = 'expiration';
    public readonly PROVIDER_STATUS = 'status';
    public readonly PROVIDER_OBSERVATIONS = 'observations';
    public readonly PROVIDER_OBSERVATIONS_MAX_LENGTH = 500;
    public readonly PROVIDER_RANGE = 'providerRange'
    public readonly PROVIDER_LOCATIONS = 'providersLocations';
    public readonly PROVIDER_TITLE = 'providerTitle';
    public readonly EXTRA_LOCATION_ID = 'id';
    public readonly EXTRA_LOCATION = 'location';
    public readonly LOCATION_ID = "id";
    public readonly EXTRA_LOCATION_SEARCHING = "extraLocationSearching";

    public dspReady = false;

    _specialtyProfessionalTypes: Subscription;
    isLoadingGettingTypeDocuments$: Observable<boolean>;
    _isLoadingGettingProviders: Subscription;
    _isLoadingUpdatingProviders: Subscription;
    _isLoadingCreatingProviders: Subscription;
    _providers: Subscription;
    _mdp: Subscription;
    _input: Subscription;
    _typeDocuments: Subscription;
    providerWithoutNumberCheck: boolean = false;
    providerWithoutStreetNumber: boolean = false;
    evolutionIdGettingMedia: number;
    actualProviderLocation;
    actualProviderNationality;
    inputDSPmode = '#F2EFF8';
    textDSPmode = '#fff';
    actualProviderSpecialties: string[] = [null];
    displayedColumns: string[] = [
        'id',
        'name',
        'cuit',
        'mobilePhoneNumber',
        'email',
        'address',
        'active',
        'user',
        'exchangeStatus',
        'actions'
    ];
    medicalLicensesTypes = [
        {
            id: MedicalLicenseTypes.PROVINCIAL,
            name: 'Provincial',
        },
        {
            id: MedicalLicenseTypes.NACIONAL,
            name: 'Nacional',
        },
        {
            id: MedicalLicenseTypes.INDEFINIDO,
            name: 'Indefinido',
        }
    ];

    specialtyProfessionalTypes: SpecialtyProfessionalType[] = [];

    providerStatusArray = [
        {
            id: 1,
            name: 'Activo',
            value: PROVIDER_STATUS.ACTIVE
        },
        {
            id: 2,
            name: 'Posible',
            value: PROVIDER_STATUS.POSSIBLE
        },
        {
            id: 3,
            name: 'Cancelado',
            value: PROVIDER_STATUS.CANCELLED
        },
        {
            id: 4,
            name: 'Inactivo',
            value: PROVIDER_STATUS.INACTIVE
        }
    ];
    swalWithCustomizeButtons: any;
    providerAddress: ProviderToInput = {
        city: null,
        province: null
    }
    public isDSP = false;
    private dspOpened = false; // evita re-abrir/duplicar
    extraLocationsOnSelect: [Location[]];
    actualProviderExtraLocations = [];
    locationsOnSelect: Location[];
    nationalitiesOnSelect: Location[];
    specialtiesOnSelect: [Specialty[]];
    inputFilterValue = ''
    dataSource;
    metaDataProviders: MetaDataProviders;
    formAddOrModifyProvider: FormGroup;
    addingProvider: boolean = false;
    updatingProvider: boolean = false;
    isLoadingUpdatingProviders: boolean;
    isLoadingGettingProviders: boolean;
    isLoadingCreatingProviders: boolean;
    loadingAddFile: boolean = false;
    fileChanged: boolean;
    entitiesFiles = EntitiesFiles;
    typeDocuments: TypeDocument[];
    _dialogRef;
    actualProvider: Provider;
    photoProviderB64: string = null;;
    actualPhotoBase64;
    specialties: Specialty[];
    index: number = 0;
    providerLatitude: number;
    providerLongitude: number;
    locationToInputAddress: Address | AddressData;
    addressInputModel: Address | AddressData | null = null;
    providerStatusSelected: PROVIDER_STATUS
    DEFAULT_LICENSE_ID = 10000000;
    DEFAULT_SPECIALTY_ID = 19 // Cuidador

    private _selectedLocation: AddressData;
    private _providerProvince
    private _providerLocation
    private destroy$ = new Subject<void>();

    get selectedLocation() {
        return this._selectedLocation
    }

    set selectedLocation(value: AddressData | null) {
        // obtenemos el FormGroup de address
        const fg = this.formAddOrModifyProvider.get(this.ADDRESS) as FormGroup;

        if (!value) {
            // si el child emitió null, limpiamos todos los campos de address
            fg.patchValue({
                [this.ADDRESS_STREET]: '',
                [this.ADDRESS_STREETNUMBER]: '',
                [this.PROVIDER_LOCATION_LATITUDE]: null,
                [this.PROVIDER_LOCATION_LONGITUDE]: null
            });
            this._selectedLocation = null;
            return;
        }

        // concatenamos calle + número para mostrarlo en el único input
        const full = [value.street, value.streetNumber]
            .filter(v => v != null && v !== '')
            .join(' ');

        // parcheamos tanto el display (street) como el number y las coords
        fg.patchValue({
            [this.ADDRESS_STREET]: full,
            [this.ADDRESS_STREETNUMBER]: value.streetNumber || '',
            [this.PROVIDER_LOCATION_LATITUDE]: value.latitude,
            [this.PROVIDER_LOCATION_LONGITUDE]: value.longitude
        });

        // rompemos referencia para detectar cambios
        this._selectedLocation = { ...value };
    }

    get providerLocation() {
        return this._providerLocation
    }
    set providerLocation(value) {
        this._providerLocation = value?.name
        this.providerAddress.province = value?.province?.name
        this.providerAddress.city = value?.name
    }
    get providerProvince() {
        return this._providerProvince
    }
    set providerProvince(value) {
        this._providerProvince = value
    }
    get name() {
        return this.formAddOrModifyProvider.get(this.NAME).value;
    }
    get surname() {
        return this.formAddOrModifyProvider.get(this.SURNAME).value;
    }
    get active() {
        return this.formAddOrModifyProvider.get(this.ACTIVE).value;
    }
    get cuit() {
        return this.formAddOrModifyProvider.get(this.CUIT).value;
    }
    get email() {
        return this.formAddOrModifyProvider.get(this.EMAIL).value;
    }
    get phonenumber() {
        return this.formAddOrModifyProvider.get(this.MOBILE_PHONENUMBER).value;
    }
    get birthdate() {
        return this.formAddOrModifyProvider.get(this.BIRTHDATE).value;
    }
    get expiration() {
        return this.formAddOrModifyProvider.get(this.EXPIRATION_DATE).value;
    }
    get providerRange() {
        return this.formAddOrModifyProvider.get(this.PROVIDER_RANGE).value;
    }
    get providerStatus() {
        return this.formAddOrModifyProvider.get(this.PROVIDER_STATUS).value;
    }
    get observations() {
        return this.formAddOrModifyProvider.get(this.PROVIDER_OBSERVATIONS).value;
    }
    get street() {
        return this.addressFormGroup.get(this.ADDRESS_STREET).value;
    }
    get withoutNumber() {
        return this.addressFormGroup.get(this.ADDRESS_WITHOUT_STREET_NUMBER).value;
    }
    get streetNumber() {
        return this.addressFormGroup.get(this.ADDRESS_STREETNUMBER).value;
    }
    get flat() {
        return this.addressFormGroup.get(this.ADDRESS_FLAT).value;
    }
    get flatExtra() {
        return this.addressFormGroup.get(this.ADDRESS_FLATEXTRA).value;
    }
    get latitude() {
        return this.addressFormGroup.get(this.PROVIDER_LOCATION_LATITUDE).value;
    }
    get longitude() {
        return this.addressFormGroup.get(this.PROVIDER_LOCATION_LONGITUDE).value;
    }
    set latitude(value) {
        this.addressFormGroup.get(this.PROVIDER_LOCATION_LATITUDE).value;
    }
    set longitude(value) {
        this.addressFormGroup.get(this.PROVIDER_LOCATION_LONGITUDE).value;
    }
    get floor() {
        return this.addressFormGroup.get(this.ADDRESS_FLOOR).value;
    }
    get location() {
        return this.addressFormGroup.get(this.ADDRESS_LOCATION).value;
    }
    get nationality() {
        return this.formAddOrModifyProvider.get(this.NATIONALITY).value;
    }
    get addressFormGroup() {
        return this.formAddOrModifyProvider.get(this.ADDRESS);
    }
    get specialtiesFormArray(): FormArray {
        return this.formAddOrModifyProvider.get(this.SPECIALTIES) as FormArray;
    }
    getMedicalLicensesFormArray(specialtyIndexFormArray: number): FormArray {
        return this.specialtiesFormArray.at(specialtyIndexFormArray).get(this.MEDICAL_LICENSES) as FormArray;
    }
    get providerLocationsFormArray(): FormArray {
        return this.formAddOrModifyProvider.get(this.PROVIDER_LOCATIONS) as FormArray;
    }
    get providerTitle() {
        return this.formAddOrModifyProvider.get(this.PROVIDER_TITLE).value;
    }
    set providerTitle(val: any) {
        this.formAddOrModifyProvider.get(this.PROVIDER_TITLE).setValue(val);
    }
    get addressLocationSearching() {
        return this.formAddOrModifyProvider.get(this.ADDRESS_LOCATION_SEARCHING).value;
    }
    set addressLocationSearching(val: any) {
        this.formAddOrModifyProvider.get(this.ADDRESS_LOCATION_SEARCHING).setValue(val);
    }
    get profileMedia() {
        return this.formAddOrModifyProvider.get(this.PROFILE_MEDIA).value;
    }
    set profileMedia(val: any) {
        this.formAddOrModifyProvider.get(this.PROFILE_MEDIA).setValue(val);
    }
    get documentMediaFront() {
        return this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_FRONT).value;
    }
    set documentMediaFront(val: any) {
        this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_FRONT).setValue(val);
    }
    get documentMediaBack() {
        return this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_BACK).value;
    }
    set documentMediaBack(val: any) {
        this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_BACK).setValue(val);
    }
    set observations(value) {
        this.formAddOrModifyProvider.get(this.PROVIDER_OBSERVATIONS).setValue(value);
    }

    constructor(
        public generalService: GeneralService,
        public patientsService: PatientsService,
        public providersService: ProvidersService,
        public dialog: MatDialog,
        public fileUtilitiesService: FileUtilitiesService,
        public nomenclatorFacade: NomenclatorFacade,
        private authService: AuthenticationService,
        private formBuilder: FormBuilder,
        private alertService: AlertService,
        private entitiesFacade: EntitiesFacade,
        public evolutionsFacade: EvolutionsFacade,
        private router: Router,
        @Inject(DOCUMENT) private doc: Document,
        @Inject(DOCUMENT) private document: Document,
        @Optional() private hostDialogRef?: MatDialogRef<ProvidersManagementComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: { mode?: string; providerId?: number },
    ) {
        this.isLoadingGettingTypeDocuments$ = this.entitiesFacade.isLoadingGettingTypeDocuments$();
    }

    //GUARDAMOS CALLE Y NUMERO EN EL FORMADDRESS
    onSelectAddress(event: AddressData | null) {

        if (!event) {
            this.providerWithoutStreetNumber = false;
            this.providerWithoutNumberCheck = false;
            // deshabilitamos el checkbox:
            this.formAddOrModifyProvider
                .get(this.ADDRESS)
                .get(this.ADDRESS_WITHOUT_STREET_NUMBER)!.disable();
            this.selectedLocation = null;
            return;
        }

        const withoutNum = !event.streetNumber;  // true si streetNumber === '' o null

        this.providerWithoutStreetNumber = withoutNum;
        // cada vez que cambia la dirección, reseteo el check:
        this.providerWithoutNumberCheck = false;

        // habilito/deshabilito el checkbox según si falta número
        const withoutNumCtrl = this.formAddOrModifyProvider.get(this.ADDRESS)
            .get(this.ADDRESS_WITHOUT_STREET_NUMBER)!;
        if (withoutNum) {
            withoutNumCtrl.enable();
        } else {
            withoutNumCtrl.disable();
            withoutNumCtrl.setValue(false, { emitEvent: false });
        }

        // finalmente guardo la dirección
        this.selectedLocation = event;
    }


    onSelectCity(locationObj: Location) {

        // setear tu variable y tu form control:
        this.providerLocation = locationObj;         // dispara tu setter
        this.getAddressFormGroup().get(this.ADDRESS_LOCATION)
            ?.setValue(locationObj);                   // guarda el objeto completo
    }

    ngOnInit(): void {
        this.isDSP = this.dialogData?.mode === 'DSP';
        this.swalWithCustomizeButtons = Swal.mixin({
            customClass: {
                confirmButton: 'btnSwalConfirm',
                cancelButton: 'btnSwalCancel'
            },
            buttonsStyling: true
        })
        this.dataSource = new MatTableDataSource();
        this.suscribeToData();
        this.toggleDspClass(this.isDSP);
    }

    ngAfterViewInit(): void {
        // 1) merge de TODOS los QueryList.changes que te interesan
        const changes$ = merge(
          this._textInputs.changes,
          this._numberInputs.changes,
          this._dateInputs.changes,
          this._selectSearchingDinamicInputs.changes,
          this._selectSearchingInputs.changes,
          this._selectInputs.changes,
          this._addressInputs.changes,
        );

        // 2) Dispara una vez al inicio y luego en cada cambio; auditTime(0) agrupa emisiones del mismo tick
        changes$
          .pipe(
            startWith(null),
            auditTime(0),
            takeUntil(this.destroy$)
          )
          .subscribe(() => this.applyDSPDefaults());
      }

    toggleDspClass(on: boolean): void {
        const body = this.doc?.body;
        if (!body) return;
        if (on) body.classList.add('dsp-mode');
        else body.classList.remove('dsp-mode');
      }

    ngOnDestroy(): void {
        this.isDSP = false;
        this.destroy$.next();
        this.destroy$.complete();
        this.toggleDspClass(false);
        this._typeDocuments.unsubscribe();
        if (!!this._isLoadingGettingProviders) {
            this._isLoadingGettingProviders.unsubscribe();
        }
        if (!!this._isLoadingUpdatingProviders) {
            this._isLoadingUpdatingProviders.unsubscribe();
        }
        if (!!this._isLoadingCreatingProviders) {
            this._isLoadingCreatingProviders.unsubscribe();
        }
        if (!!this._providers) {
            this._providers.unsubscribe();
        }
        if (!!this._mdp) {
            this._mdp.unsubscribe();
        }
        if (!!this._input) {
            this._input.unsubscribe();
        }
        if (!!this._specialtyProfessionalTypes) {
            this._specialtyProfessionalTypes.unsubscribe();
        }
    }

    applyDSPDefaults(): void {
        if (!this.isDSP || !this._textInputs) return;

        this._textInputs.forEach((cmp) => {
            if (cmp.inputBgColor == null) cmp.inputBgColor = this.inputDSPmode;
            if (cmp.inputTextColor == null) cmp.inputTextColor = this.textDSPmode;
        });
        this._numberInputs.forEach((cmp) => {
            if (cmp.inputBgColor == null) cmp.inputBgColor = this.inputDSPmode;
            if (cmp.inputTextColor == null) cmp.inputTextColor = this.textDSPmode;
        });
        this._dateInputs.forEach((cmp) => {
            if (cmp.inputBgColor == null) cmp.inputBgColor = this.inputDSPmode;
            if (cmp.inputTextColor == null) cmp.inputTextColor = this.textDSPmode;
        });
        this._selectSearchingDinamicInputs.forEach((cmp) => {
            if (cmp.inputBgColor == null) cmp.inputBgColor = this.inputDSPmode;
            if (cmp.inputTextColor == null) cmp.inputTextColor = this.textDSPmode;
        });
        this._selectSearchingInputs.forEach((cmp) => {
            if (cmp.inputBgColor == null) cmp.inputBgColor = this.inputDSPmode;
            if (cmp.inputTextColor == null) cmp.inputTextColor = this.textDSPmode;
        });
        this._selectInputs.forEach((cmp) => {
            if (cmp.inputBgColor == null) cmp.inputBgColor = this.inputDSPmode;
            if (cmp.inputTextColor == null) cmp.inputTextColor = this.textDSPmode;
        });
        this._addressInputs.forEach((cmp) => {
            if (cmp.inputBgColor == null) cmp.inputBgColor = this.inputDSPmode;
            if (cmp.inputTextColor == null) cmp.inputTextColor = this.textDSPmode;
        });
    }

    suscribeToData() {
        console.log('Subscribing to data in ProvidersManagementComponent',this.isDSP);

        this._typeDocuments = this.entitiesFacade.getTypeDocuments$().subscribe(typeDocuments => {
            if (!!typeDocuments) {
                this.typeDocuments = typeDocuments;
            } else {
                this.entitiesFacade.loadTypeDocuments();
            }
        })
        this._isLoadingGettingProviders = this.entitiesFacade.isLoadingGettingProviders$()
            .subscribe(loading => this.isLoadingGettingProviders = loading);
        this._isLoadingUpdatingProviders = this.entitiesFacade.isLoadingUpdatingProviders$()
            .subscribe(loading => this.isLoadingUpdatingProviders = loading);
        this._isLoadingCreatingProviders = this.entitiesFacade.isLoadingCreatingProviders$()
            .subscribe(loading => this.isLoadingCreatingProviders = loading);
        this._mdp = this.entitiesFacade.getMetaDataProviders$().subscribe((mdp) => {
            this.metaDataProviders = mdp;
            console.log('Meta data Providers', mdp)
        })

        let providerQPS: ProvidersQPS = {
            page: 0,
            size: this.isDSP ? 1000 : 10,
            active: true,
            expand: ['user']
        };
        if(!this.isDSP){
            this.entitiesFacade.loadProviders(providerQPS)
        }
        this._providers = this.entitiesFacade.getProviders$().subscribe((providers: Provider[]) => {

            if (providers == null && !this.isLoadingGettingProviders) {
                this.entitiesFacade.loadProviders(providerQPS)
            }
            else {
                this.dataSource.data = providers;
                if (this.isDSP && !this.dspOpened && this.dialogData?.providerId != null) {
                    const found = providers?.find(p => p.id === this.dialogData!.providerId);
                    if (found) {
                        this.openModifyProviderDialog(found);
                        this.dspOpened = true;
                    }
                }
            }
        })

        // Getting specialty professional types
        this._specialtyProfessionalTypes = this.nomenclatorFacade.getSpecialtyProfessionalTypes$().subscribe( spt => {
            if(!!spt) {
                this.specialtyProfessionalTypes = spt;
            } else {
                this.nomenclatorFacade.loadSpecialtyProfessionalTypes();
            }
        })
    }

    openTitleDialog(event: MouseEvent, inputRef: HTMLInputElement) {
        event.preventDefault();
        event.stopPropagation();
        // Abre el input del título
        inputRef.click();
    }

    openLicenseDialog(event: MouseEvent, inputRef: HTMLInputElement) {
        event.preventDefault();
        event.stopPropagation();
        inputRef.click();
    }

    openDocumentFrontDialog(event: MouseEvent, inputRef: HTMLInputElement) {
        event.preventDefault();
        event.stopPropagation();
        inputRef.click();
    }

    openDocumentBackDialog(event: MouseEvent, inputRef: HTMLInputElement) {
        event.preventDefault();
        event.stopPropagation();
        inputRef.click();
    }

    openProfileDialog(event: MouseEvent, inputRef: HTMLInputElement) {
        event.preventDefault();
        event.stopPropagation();
        inputRef.click();
    }

    calculateNameLocation(location: Location) {
        let string = '';
        string += location ? location.name : '';
        string += location && location.province && location.province.name ? ', ' + location.province.name : '';
        return string;
    }

    calculateNameNationality(nationality: Nationality) {
        return !!nationality?.name ? nationality.name : '';
    }

    loadNationalitiesByName = (value: string) => {
        const qps: NationsQPS = {
            name_like: value
        }
        return this.entitiesFacade.loadNations(qps)
    }

    onPaginatorEvent(event: PageEvent) {
        let providerQPS: ProvidersQPS = this.metaDataProviders;
        providerQPS.size = event.pageSize;
        providerQPS.page = event.pageIndex;
        this.entitiesFacade.loadProviders(providerQPS);
    }

    onSearch(st: string) {
        let providerQPS: ProvidersQPS = this.metaDataProviders;
        if (st.length == 0) {
            delete providerQPS.fullName_like;
        } else {
            providerQPS.fullName_like = st;
        }
        providerQPS.page = 0;
        this.entitiesFacade.loadProviders(providerQPS)
    }

    onChangeCheckbox(e) {
        let providerQPS: ProvidersQPS = this.metaDataProviders;
        providerQPS.page = 0;
        providerQPS.active = e.checked;
        this.entitiesFacade.loadProviders(providerQPS);
    }

    onClickCreateUser(provider) {
        this.swalWithCustomizeButtons.fire({
            title: 'Atención',
            text: `¿Seguro desea crear un usuario asociado al prestador ${provider?.surname || provider?.name ? provider?.surname + ' ' + provider.name : ''} ?`,
            icon: 'question',
            confirmButtonText: 'CONFIRMAR',
            cancelButtonText: 'CANCELAR',
            showLoaderOnConfirm: true,
            showCancelButton: true,
            reverseButtons: true,
            preConfirm: () => {
                return this.authService.postUserProvider({ id: provider.id })
                    .toPromise()
                    .then((_) => {
                        this.swalWithCustomizeButtons.fire({
                            title: 'Cuenta creada correctamente ',
                            text: 'Se le ha enviado un mail a la casilla de correo del usuario para que active la cuenta',
                            icon: 'success',
                        });
                    })
                    .then(_ => this.entitiesFacade.loadProviders(this.metaDataProviders))
                    .catch((err) => {
                        console.log('error', err);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        })
    }

    onClickResendEmailUser(provider) {
        this.swalWithCustomizeButtons.fire({
            title: 'Atención',
            text: `¿Seguro desea volver a activar la cuenta del usuario ${provider?.surname || provider?.name ? provider?.surname + ' ' + provider.name : ''} ?`,
            icon: 'question',
            confirmButtonText: 'CONFIRMAR',
            cancelButtonText: 'CANCELAR',
            showLoaderOnConfirm: true,
            showCancelButton: true,
            reverseButtons: true,
            preConfirm: () => {
                return this.authService.postUserProvider({ id: provider.id })
                    .toPromise()
                    .then((_) => {
                        this.swalWithCustomizeButtons.fire({
                            title: 'Cuenta creada correctamente ',
                            text: 'Se le ha enviado un mail a la casilla de correo del usuario para que active la cuenta',
                            icon: 'success',
                        });
                    })
                    .then(_ => this.entitiesFacade.loadProviders(this.metaDataProviders))
                    .catch((err) => {
                        console.log('error', err);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        })
    }

    onClickActivateUser(provider) {
        this.swalWithCustomizeButtons.fire({
            title: 'Atención',
            text: `¿Seguro desea volver a activar la cuenta del usuario ${provider?.surname || provider?.name ? provider?.surname + ' ' + provider.name : ''} ?`,
            icon: 'question',
            confirmButtonText: 'CONFIRMAR',
            cancelButtonText: 'CANCELAR',
            showLoaderOnConfirm: true,
            showCancelButton: true,
            reverseButtons: true,
            preConfirm: () => {
                return this.authService.patchUser({ active: true }, provider.user.id)
                    .toPromise()
                    .then((_) => {
                        this.swalWithCustomizeButtons.fire({
                            text: 'Cuenta activada correctamente ',
                            icon: 'success',
                        });
                    })
                    .then(_ => this.entitiesFacade.loadProviders(this.metaDataProviders))
                    .catch((err) => {
                        console.log('error', err);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        })
    }

    onClickDeactivateUser(provider) {
        this.swalWithCustomizeButtons.fire({
            title: 'Atención',
            text: `¿Seguro desea desactivar la cuenta del usuario ${provider?.surname || provider?.name ? provider?.surname + ' ' + provider.name : ''} ?`,
            icon: 'question',
            confirmButtonText: 'CONFIRMAR',
            cancelButtonText: 'CANCELAR',
            showLoaderOnConfirm: true,
            showCancelButton: true,
            reverseButtons: true,
            preConfirm: () => {
                return this.authService.patchUser({ active: false }, provider.user.id)
                    .toPromise()
                    .then((_) => {
                        this.swalWithCustomizeButtons.fire({
                            text: 'Cuenta activada correctamente ',
                            icon: 'success',
                        });
                    })
                    .then(_ => this.entitiesFacade.loadProviders(this.metaDataProviders))
                    .catch((err) => {
                        console.log('error', err);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        })
    }

    onClickDeleteProvider(provider) {
        let providerToDelete: PatchProviderBody = {
            active: !provider.active
        };
        let text: string;
        let text2: string;
        if (provider.active) {
            text = 'eliminar';
            text2 = 'eliminado';
        } else {
            text = 'restaurar';
            text2 = 'restaurado';
        }
        this.swalWithCustomizeButtons.fire({
            title: 'Atención',
            text: `¿Está seguro que desea ` + text + ` el prestador "` + (provider?.surname || provider?.name ? provider?.surname + ' ' + provider.name : '') + `" ?`,
            icon: 'question',
            confirmButtonText: 'CONFIRMAR',
            cancelButtonText: 'CANCELAR',
            showLoaderOnConfirm: true,
            showCancelButton: true,
            reverseButtons: true,
            preConfirm: () => {
                return this.entitiesFacade.updateProvider(providerToDelete, provider.id).toPromise()
                    .then((_) => {
                        this.swalWithCustomizeButtons.fire({
                            text: 'Prestador ' + text2 + ' correctamente',
                            icon: 'success',
                        });
                    })
                    .catch((err) => {
                        console.log('error', err);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        })
    }

    calculateProviderStatus(e: any): void {
        switch (e) {
            case '1':
                this.providerStatusSelected = PROVIDER_STATUS.ACTIVE;
                break;
            case '2':
                this.providerStatusSelected = PROVIDER_STATUS.POSSIBLE;
                break;
            case '3':
                this.providerStatusSelected = PROVIDER_STATUS.CANCELLED;
                break;
            default:
                console.warn('Valor no reconocido:', e);
                break;
        }
    }


    onClickCreateOrModifyProvider() {
        if (this.formAddOrModifyProvider.invalid) {
            this.formAddOrModifyProvider.markAllAsTouched();
            return;
        }
        // incluye también los valores de los controles disabled
        const formValue = this.formAddOrModifyProvider.getRawValue();
        formValue.files = [];

        delete formValue.address.locationSearching;
        delete formValue.nationalitySearching;
        delete formValue.locationSearching;

        formValue.providersLocations = formValue.providersLocations
            .map(provLocation => {
                delete provLocation.location.extraLocationSearching;
                return { ...provLocation };
            })
            .filter(pl => pl.location.id !== null && pl.location.id !== undefined);
        // TODO: VER ESTO
        formValue.address.location = { id: parseInt(this.formAddOrModifyProvider.get(this.ADDRESS).get(this.ADDRESS_LOCATION).value) };
        formValue.address.street = this.selectedLocation?.street ? this.selectedLocation?.street : this.actualProvider?.address?.street ? this.actualProvider.address.street : null;
        // formValue.address.streetNumber = this.selectedLocation?.streetNumber ? this.selectedLocation?.streetNumber : this.actualProvider?.address?.streetNumber ? this.actualProvider.address.streetNumber : null;
        formValue.address.streetNumber = this.selectedLocation?.streetNumber
            ?? this.actualProvider?.address?.streetNumber
            ?? null;

        if (this.formAddOrModifyProvider.get(this.PROVIDER_TITLE)?.value) {
            formValue.files.push(...this.formAddOrModifyProvider.get(this.PROVIDER_TITLE).value.flat(1));
            delete formValue.providerTitle;
        }
        if (this.formAddOrModifyProvider.get(this.PROFILE_MEDIA)?.value) {
            formValue.files.push(this.formAddOrModifyProvider.get(this.PROFILE_MEDIA)?.value);
            delete formValue.profile;
        }
        if (this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_FRONT)?.value !== null) {
            formValue.files.push(this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_FRONT)?.value);
        }
        if (this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_BACK)?.value !== null) {
            formValue.files.push(this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_BACK).value);
        }
        delete formValue.documentMediaFront;
        delete formValue.documentMediaBack;

        if (Array.isArray(formValue.specialties)) {
            formValue.specialties = formValue.specialties.map(origSpec => {
                if (!origSpec) return origSpec;

                // 1) Parse the specialty ID
                const id = parseInt(origSpec.id, 10);

                // 2) Discard internal properties that we don't send
                const { specialtiesSearching, ...restSpec } = origSpec;

                // 3) Process medicalLicenses
                const medicalLicenses = Array.isArray(restSpec.medicalLicenses)
                    ? restSpec.medicalLicenses
                        .map(ml => {

                            // a) Convert type to object { id: number }
                            const typeObj = ml?.type
                                ? { id: parseInt(ml.type as any, 10) }
                                : null;

                            // b) Extract mediaUUID from the first entry of medicalLicenseMedia
                            const mediaUUID = ml.medicalLicenseMedia?.[0]?.mediaUUID || ml.mediaUUID;

                            // If we are adding a provider (POST) and it is the "default" license,
                            // we force id = null so backend creates a new resource.
                            // If we are updating (PATCH), preserve the incoming id even if it equals DEFAULT_LICENSE_ID.
                            const licenseId = this.updatingProvider
                                ? (ml.id === this.DEFAULT_LICENSE_ID && ml.value != null && ml.value !== '' && ml.value !== '0' ? null : ml.id)
                                : ml.id;

                            // c) Always return the object, injecting type and mediaUUID
                            return {
                                ...ml,
                                id: licenseId,           // <–– Here we cancel the default
                                ...(typeObj ? { type: typeObj } : {}),
                                ...(mediaUUID ? { mediaUUID } : {})
                            };
                        })
                        // 4) Filter only valid licenses
                        .filter(ml =>
                            !!(ml.value && ml.type)
                        )
                    : [];

                const specialtyProfessionalTypeId = origSpec.specialtyProfessionalTypeId;

                // 5) Return only what you need for the specialty
                return { id, medicalLicenses, specialtyProfessionalTypeId };
            });
        }

        formValue.status = this.providerStatusSelected ? this.providerStatusSelected : formValue.status;

        if (this.addingProvider) {
            this.entitiesFacade.addProvider(formValue).subscribe((provider) => {
                this.swalWithCustomizeButtons.fire({
                    text: 'El prestador se creó correctamente',
                    icon: 'success'
                }).then(() => {
                    this._dialogRef.close();
                }).catch(e => {
                    this._dialogRef.close();
                });
            });
        } else if (this.updatingProvider) {
            this.entitiesFacade.updateProvider(formValue, this.actualProvider.id).subscribe((provider) => {
                this.swalWithCustomizeButtons.fire({
                    text: 'El prestador se modificó correctamente',
                    icon: 'success'
                }).then(() => {
                    this._dialogRef.close();
                }).catch(e => {
                    this._dialogRef.close();
                });
            });
        }
    }

    openAddProviderDialog() {
        this.locationToInputAddress = null;
        this.locationsOnSelect = null;
        this.nationalitiesOnSelect = null;
        this.specialtiesOnSelect = [null];
        this.extraLocationsOnSelect = [null];
        this.addingProvider = true;
        this.addressInputModel = undefined;
        this.formAddOrModifyProvider = this.createAddOrModifyProviderForm();
        this.openDialog('Dar de alta a Prestador', this.addOrModifyProviderTemplate, { maxHeight: '95vh', minWidth: '60%' }, () => {
            this.addingProvider = false;
            this.updatingProvider = false;
            this.actualProviderLocation = null;
            this.actualProviderNationality = null;
            this.actualProviderSpecialties = [null];
        });
        this.getProviderMedia();
        this.formsChangesSuscribe();
    }

    formsChangesSuscribe() {
        //CUIT CHANGES
        const cuitCtrl = this.formAddOrModifyProvider.get(this.CUIT);
        cuitCtrl?.valueChanges.subscribe(val => {
            if (cuitCtrl.valid) {
                this.providersService.getCuit({ cuit: val }).subscribe({
                    next: ok => console.log('Existe CUIT?', ok),
                    error: e => console.error(e),
                });
            }
        });
    }

    openModifyProviderDialog(provider: Provider) {
        this.updatingProvider = true;
        // --- DSP MODE
        if (this.isDSP) {
            this.locationsOnSelect = null;
            this.nationalitiesOnSelect = null;
            this.specialtiesOnSelect = [null];
            this.extraLocationsOnSelect = [null];

            this.actualProvider = provider;
            this.formAddOrModifyProvider = this.createAddOrModifyProviderForm(provider);

            this.specialtiesFormArray.controls.forEach(fg => {
                fg.updateValueAndValidity({ onlySelf: true });
                fg.markAsTouched();
            });

            if (provider.address) {
                const data: AddressData = {
                    ...provider.address,
                    city: provider.address.location?.name ?? '',
                    province: provider.address.location?.province?.name ?? '',
                    country: provider?.nationality?.name ?? ''
                };
                this.addressInputModel = data;
                this.selectedLocation = data;
            } else {
                this.addressInputModel = null;
            }

            if (provider.address?.location) {
                this.providerLocation = provider.address.location;
                this.locationsOnSelect = [provider.address.location];
                this.actualProviderLocation = provider.address.location.id.toString();
            }
            if (!!provider?.nationality) {
                this.nationalitiesOnSelect = [provider.nationality];
                this.actualProviderNationality = provider.nationality.id.toString();
            }

            this.getProviderMedia();
            this.dspReady = true;
            return;
        }
        // --- END DSP MODE
        this.locationsOnSelect = null;
        this.nationalitiesOnSelect = null;
        this.specialtiesOnSelect = [null];
        this.extraLocationsOnSelect = [null];
        this.actualProvider = provider;
        this.formAddOrModifyProvider = this.createAddOrModifyProviderForm(provider);

        this.specialtiesFormArray.controls.forEach(fg => {
            fg.updateValueAndValidity({ onlySelf: true });
            fg.markAsTouched();
        });

        console.log(provider);
        if (provider.address) {
            const data: AddressData = {
                ...provider.address,
                city: provider.address.location?.name ?? '',
                province: provider.address.location?.province?.name ?? '',
                country: provider?.nationality?.name ?? ''
            };
            this.addressInputModel = data;
            this.selectedLocation = data;
        } else {
            this.addressInputModel = null;
        }

        if (provider.address?.location) {
            this.providerLocation = provider.address.location;
            this.locationsOnSelect = [provider.address.location];
            this.actualProviderLocation = provider.address.location.id.toString();
        }

        if (!!this.actualProvider?.nationality) {
            this.nationalitiesOnSelect = [provider.nationality];
            this.actualProviderNationality = provider.nationality.id.toString();
        }
        this.getProviderMedia();

        setTimeout(() => {
            if (this.isDSP) return;
            this.openDialog('Modificar Prestador', this.addOrModifyProviderTemplate, { maxHeight: '95vh', minWidth: '60%' }, () => {
                this.addingProvider = false;
                this.updatingProvider = false;
                this.actualProviderLocation = null;
                this.actualProviderNationality = null;
                this.actualProviderSpecialties = [null];
                this.actualProviderExtraLocations = [null];
            });
        }, 500);

    }

    getProviderStatus(provider: PROVIDER_STATUS) {
        return provider
    }

    createAddOrModifyProviderForm(provider?: Provider) {
        return this.formBuilder.group({
            [this.NAME]: [provider && provider.name ? provider.name : '', [CustomValidators.required('Requerido')]],
            [this.SURNAME]: [provider && provider.surname ? provider.surname : '', [CustomValidators.required('Requerido')]],
            [this.CUIT]: [provider && provider.cuit ? provider.cuit : '',
            {
                validators: [
                    CustomValidators.required('Requerido'),
                    CustomValidators.cuilCuit('Cuit inválido')
                ],
                asyncValidators: [
                    this.cuitExistsAsyncValidator(this.providersService, provider?.cuit)
                ],
                updateOn: 'blur'
            }],
            [this.MOBILE_PHONENUMBER]: [provider && provider.mobilePhoneNumber ? provider.mobilePhoneNumber : ''],
            [this.BIRTHDATE]: [provider && provider.birthDate ? provider.birthDate : ''],
            [this.EMAIL]: [provider && provider.email ? provider.email : '', [CustomValidators.email('Email inválido')]],
            [this.NATIONALITY]: [provider && provider.nationality ? provider.nationality : '',],
            [this.NATIONALITY_SEARCHING]: [''],
            [this.ADDRESS]: this.createAddressForm(provider && provider.address ? provider.address : null),
            [this.ADDRESS_LOCATION_SEARCHING]: [''],
            [this.PROVIDER_LOCATIONS]: this.formBuilder.array(this.createExtraLocationsFormArray(provider)),
            [this.PROVIDER_TITLE]: [[]],
            [this.SPECIALTIES]: this.formBuilder.array(this.createSpecialtyFormArray(provider)),
            [this.PROVIDER_OBSERVATIONS]: [provider && provider.observations ? provider.observations : '', [CustomValidators.maxLength(this.PROVIDER_OBSERVATIONS_MAX_LENGTH, "Test")]],
            [this.PROVIDER_STATUS]: [provider && provider.status ? provider.status : '', [CustomValidators.required('Requerido')]],
            [this.PROVIDER_RANGE]: [''],
            [this.PROFILE_MEDIA]: [null],
            [this.DOCUMENT_MEDIA_FRONT]: [null],
            [this.DOCUMENT_MEDIA_BACK]: [null],
            mediaUUID: [provider && provider.mediaUUID ? provider.mediaUUID : null]
        })
    }

    cuitExistsAsyncValidator(
        providers: ProvidersService,
        originalCuit?: number
    ): AsyncValidatorFn {
        return (control: AbstractControl) => {
            const cuit = (control.value ?? '').toString().replace(/\D/g, '');
            if (!cuit || cuit.length !== 11) return of<ValidationErrors | null>(null);

            if (originalCuit != null) {
                const orig = String(Math.trunc(originalCuit));
                if (orig === cuit) return of<ValidationErrors | null>(null);
            }

            return timer(300).pipe(
                switchMap(() => providers.getCuit({ cuit })),
                map((isRepeated: boolean) =>
                    isRepeated ? { cuitExists: 'El CUIT ingresado ya está registrado' } : null
                ),
                catchError(() => of<ValidationErrors | null>(null)),
                take(1)
            );
        };
    }

    createSpecialtyFormArray(provider?: Provider) {

        this.index = 0;
        let specialtyFormArray;

        if (!!provider?.specialties && provider.specialties.length != 0) {
            specialtyFormArray = provider.specialties.map((specialty) => {
                let specialtyForm;
                this.specialtiesOnSelect[this.index] = [specialty];
                this.actualProviderSpecialties[this.index] = specialty.id.toString();
                console.log([specialty]);
                this.index++;
                if (!!specialty) {
                    if (specialty.medicalLicenses != null && specialty.medicalLicenses.length == 0) {
                        specialty.medicalLicenses = null;
                    }
                    specialtyForm = this.createSpecialty(specialty)
                } else {
                    specialtyForm = this.createSpecialty()
                }
                console.log(specialtyForm);

                return specialtyForm;
            })
        } else {
            console.log([this.createSpecialty()]);
            specialtyFormArray = [this.createSpecialty()];
        }
        console.log(specialtyFormArray);
        return specialtyFormArray;
    }

    createAddressForm(address?: Address): FormGroup {
        const fullAddress = address
            ? [address.street, address.streetNumber].filter(Boolean).join(' ')
            : '';

        return this.formBuilder.group({
            // Hear Street + StreetNumber together
            [this.ADDRESS_STREET]: [fullAddress, [CustomValidators.required('Requerida')]],
            [this.ADDRESS_STREETNUMBER]: [address?.streetNumber ?? ''],
            [this.ADDRESS_WITHOUT_STREET_NUMBER]: [address?.withoutNumber ?? false],
            [this.ADDRESS_FLOOR]: [address?.floor ?? '', [CustomValidators.number('Debe ser un número')]],
            [this.ADDRESS_FLAT]: [address?.flat ?? ''],
            [this.ADDRESS_FLATEXTRA]: [address?.flatExtra ?? '', [CustomValidators.maxLength(this.ADDRESS_FLATEXTRA_MAX_LENGTH, 'Máximo ' + this.ADDRESS_FLATEXTRA_MAX_LENGTH + ' caracteres')]],
            [this.ADDRESS_LOCATION]: [address?.location ?? '', [CustomValidators.required('Requerida')]],
            [this.ADDRESS_LOCATION_SEARCHING]: [''],
            [this.PROVIDER_LOCATION_LATITUDE]: [''],
            [this.PROVIDER_LOCATION_LONGITUDE]: [''],
        });
    }


    openDialog(title: string, template: TemplateRef<any>, style: { minWidth: string; maxHeight: string }, afterClosed?: () => void): void {
        this._dialogRef = this.dialog.open(DialogComponent, {
            disableClose: true,
            minWidth: style.minWidth,
            maxHeight: style.maxHeight,
            data: { template, title },
        });

        this._dialogRef.afterClosed().subscribe(() => {
            this.actualProvider = null;
            if (afterClosed) {
                afterClosed();
            }
        });
    }

    onClickCloseDialog() {
        if (this.isDSP) {
            this.hostDialogRef?.close();
            if (!this.hostDialogRef) { this.dialog.closeAll(); }
        } else {
            this._dialogRef?.close();
        }
        this.addingProvider = false;
        this.updatingProvider = false;
        this.actualProviderLocation = null;
        this.actualProviderSpecialties = [null];
    }

    getLinkMap(provider: Provider): string {
        let link = 'https://www.google.com/maps/place/';
        link += provider.address.street ? provider.address.street : '';
        link += ' ' + (provider.address.streetNumber ? provider.address.streetNumber : '');
        link += ',' + (provider.address.location && provider.address.location.name ? provider.address.location.name : '');
        link += ',' + (provider.address.location && provider.address.location.province && provider.address.location.province.name ? provider.address.location.province.name : '');
        return link;
    }

    getSpecialtiesByName = (value: string) => this.nomenclatorFacade.loadSpecialties({ name_like: value })

    createSpecialty(specialty?: Specialty): FormGroup {
        const form = this.formBuilder.group({
            [this.SPECIALTIES_ID]: [ specialty?.id ?? '', CustomValidators.required('Requerida') ],
            [this.SPECIALTIES_SEARCHING]: [''],
            [this.MEDICAL_LICENSES]: this.formBuilder.array(this.createMedicalLicenseFormArray(specialty)),
            [this.SPECIALTY_PROFESSIONAL_TYPE_ID]: [ specialty?.specialtyProfessionalType?.id?.toString() ?? '' ],
        }, {
            validators: CustomValidators.requireMedicalLicenseIfNot(this.DEFAULT_SPECIALTY_ID)
        });

        // Cada vez que cambies de especialidad, re-creamos el array de licencias por si cambió a la default
        form.get(this.SPECIALTIES_ID)!.valueChanges.subscribe(rawId => {
            const specId = +rawId;
            const mlFA = form.get(this.MEDICAL_LICENSES) as FormArray;

            // Reconstruir según la lógica anterior
            const dummySpec: Specialty = { id: specId, medicalLicenses: specialty?.medicalLicenses } as any;
            const newLicenses = this.createMedicalLicenseFormArray(dummySpec);

            mlFA.clear();
            newLicenses.forEach(ctrl => mlFA.push(ctrl));

            form.updateValueAndValidity({ onlySelf: true });
        });

        // Y cuando cambie cualquier licencia, revalidamos
        (form.get(this.MEDICAL_LICENSES) as FormArray).valueChanges.subscribe(_ => {
            form.updateValueAndValidity({ onlySelf: true });
        });

        return form;
    }


    createMedicalLicenseFormArray(specialty?: Specialty): FormGroup[] {
        const isDefault = specialty?.id === this.DEFAULT_SPECIALTY_ID;
        let licensesToBuild: MedicalLicense[] = [];

        if (specialty?.medicalLicenses?.length) {
            // Si es la 'DEFAULT' specialty y estamos creando un nuevo prestador,
            // omitimos la matrícula placeholder (DEFAULT_LICENSE_ID) para que el back
            // cree una nueva matrícula en POST. Si estamos en modo actualización (PATCH),
            // preservamos la matrícula incluso si tiene el id placeholder.
            if (isDefault && this.addingProvider) {
                // filtramos solo las matrículas “reales” (no la DEFAULT_LICENSE_ID)
                const reales = specialty.medicalLicenses.filter(
                    ml => ml.id !== this.DEFAULT_LICENSE_ID
                );
                if (reales.length) {
                    licensesToBuild = reales;
                }
            } else {
                licensesToBuild = specialty.medicalLicenses;
            }
        }

        // Si no tenemos NINGUNA matrícula válida, siempre arrancamos con un slot vacío
        if (licensesToBuild.length === 0) {
            return [this.createMedicalLicense(true)];
        }

        return licensesToBuild.map(ml =>
            this.createMedicalLicense(false, ml)
        );
    }


    createMedicalLicense(disabled: boolean, medicalLicense?: MedicalLicense): FormGroup {
        const hasValidity = !!medicalLicense?.validity;

        const form = this.formBuilder.group({
            [this.MEDICAL_LICENSE_ID]: [!!medicalLicense?.id ? medicalLicense.id : null],
            [this.MEDICAL_LICENSE_VALUE]: [!!medicalLicense?.value ? medicalLicense.value : ''],
            [this.MEDICAL_LICENSE_VALIDITY]: [!!medicalLicense?.validity ? medicalLicense.validity : ''],
            [this.MEDICAL_LICENSE_TYPE]: [!!medicalLicense?.type ? medicalLicense.type : ''],
            [this.MEDICAL_LICENSE_MEDIA]: [medicalLicense?.medicalLicenseMedia ? medicalLicense.medicalLicenseMedia : []],
            // —> Only used in providerPATCH like param
            mediaUUID: [medicalLicense?.mediaUUID ?? null],
            expirationDate: [hasValidity]
        });

        // si NO trae validity, deshabilitamos el campo
        if (!hasValidity) {
            form.get('validity')!.disable({ emitEvent: false });
        }

        form.get('expirationDate')!
            .valueChanges
            .subscribe(checked => {
                const valCtrl = form.get('validity')!;
                if (checked) {
                    valCtrl.enable({ emitEvent: false });
                } else {
                    valCtrl.disable({ emitEvent: false });
                    valCtrl.reset();        // limpia valor anterior
                }
            });
        return form
    }

    onSelectSpecialty(i: number) {

        const mlFA = this.getMedicalLicensesFormArray(i);

        // 1) Si estaba deshabilitado, lo volvemos a habilitar
        if (mlFA.disabled) {
            mlFA.enable({ emitEvent: false });
        }

        // 2) Limpiamos el array y creamos un form en blanco
        mlFA.clear();
        mlFA.push(this.createMedicalLicense(true));

        // 3) Revalidamos el grupo de specialty
        const fg = this.getSpecialtiestFormGroup(i);
        fg.updateValueAndValidity({ onlySelf: true });
        fg.markAsTouched();    // para que salga el mensaje de error inmediatamente
    }

    addSpecialty(): void {
        this.specialtiesFormArray.push(this.createSpecialty());
    }

    /*     removeSpecialty(index: number): void {
            this.specialtiesFormArray.removeAt(index);
            this.specialtiesOnSelect.splice(index, 1);
            this.actualProviderSpecialties.splice(index, 1);
        } */

    addMedicalLicense(specialtyIndex: number) {
        const mlFA = this.getMedicalLicensesFormArray(specialtyIndex);
        mlFA.push(this.createMedicalLicense(true));
        this.touchAndValidateSpecialtyGroup(specialtyIndex);
    }

    /*     removeMedicalLicense(specialtyIndex: number, index: number): void {
            this.getMedicalLicensesFormArray(specialtyIndex).removeAt(index);
        } */

    /**
 * Antes de quitar una specialty, contamos cuántos archivos hay en todas sus licenses.
 * Si hay >0, mostramos confirmación; si no, borramos directo.
 */
    onClickRemoveSpecialty(ev: Event, i: number) {
        const mlFA = this.getMedicalLicensesFormArray(i);
        // contamos archivos en cada license
        const totalFiles = mlFA.controls
            .map(ctrl => (ctrl.get(this.MEDICAL_LICENSE_MEDIA).value as File[]).length)
            .reduce((sum, n) => sum + n, 0);

        if (totalFiles > 0) {
            this.swalWithCustomizeButtons.fire({
                title: 'Atención',
                text: `Esta especialidad tiene ${mlFA.length} matrícula(s) con ${totalFiles} archivo(s). ` +
                    `Si la elimina, se perderán esos archivos.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Eliminar',
                cancelButtonText: 'Cancelar',
                reverseButtons: true,
                allowEnterKey: false,
                allowOutsideClick: false,
                allowEscapeKey: true,
                didOpen: () => (document.activeElement as HTMLElement)?.blur(),
            }).then(result => {
                if (result.isConfirmed) {
                    this._removeSpecialty(i);
                    this.successMsg('Especialidad eliminada con sus archivos');
                }
            });
        } else {
            this._removeSpecialty(i);
        }
    }

    /**
     * Antes de quitar una single medicalLicense, comprobamos si tiene archivos.
     */
    confirmRemoveMedicalLicense(specialtyIndex: number, licenseIndex: number): void {
        const licenseCtrl = this.getMedicalLicensesFormArray(specialtyIndex).at(licenseIndex);
        const files: File[] = licenseCtrl.get(this.MEDICAL_LICENSE_MEDIA).value || [];

        if (files.length > 0) {
            this.swalWithCustomizeButtons.fire({
                title: 'Atención',
                text: `Esta matrícula tiene ${files.length} archivo(s). ` +
                    `Si la elimina, se perderán esos archivos.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar',
                reverseButtons: true
            }).then(result => {
                if (result.isConfirmed) {
                    this._removeMedicalLicense(specialtyIndex, licenseIndex);
                    this.successMsg('Matrícula eliminada con sus archivos');
                }
            });
        } else {
            this._removeMedicalLicense(specialtyIndex, licenseIndex);
        }
    }

    /**
     * Extraemos la lógica de borrado en métodos privados para poder invocarlos desde confirmaciones.
     */
    private _removeSpecialty(index: number): void {
        this.specialtiesFormArray.removeAt(index);
        this.specialtiesFormArray.markAsDirty();
        this.specialtiesOnSelect.splice(index, 1);
        this.actualProviderSpecialties.splice(index, 1);
    }

    private _removeMedicalLicense(specialtyIndex: number, licenseIndex: number) {
        const mlFA = this.getMedicalLicensesFormArray(specialtyIndex);
        mlFA.removeAt(licenseIndex);
        if (mlFA.length === 0) {
            mlFA.push(this.createMedicalLicense(true));
        }
        this.touchAndValidateSpecialtyGroup(specialtyIndex);
    }

    calculateNameMedicalLicenseType(element) {
        return element ? element.name : null;
    }

    calculateIdMedicalLicenseType(element) {
        return element ? element.id : null;
    }

    calculateNameSpecialtyProfessionalType(element) {
        return element ? element.name : null;
    }

    calculateIdSpecialtyProfessionalType(element) {
        return element ? element.id : null;
    }

    calculateIdSpecialty(element) {
        return element ? element.id : null
    }

    calculateNameSpecialty(element) {
        return element ? element.name : null;
    }

    calculateIdProviderStatus(element) {
        return element ? element.value : null
    }

    calculateNameProviderStatus(element) {
        return element ? element.name : null;
    }

    message(specialty?) {
        // console.log(specialty);

    }

    onChangeType(e) {
        // console.log(e);
    }

    onSelectProviderStatus(e) {
        // null
    }

    loadLocationsByName = (value: string) => {
        const lfp: LocationsQPS = {
            name_like: value
        };
        return this.entitiesFacade.loadLocations(lfp)
    }

    calculateIdExtraLocation(element) {
        return element ? element.id : null
    }

    calculateNameExtraLocation(element) {
        let string = '';
        string += element ? element.name : '';
        string += element && element.province && element.province.name ? ', ' + element.province.name : '';
        return string;
    }

    createLocation(extraLocation?: ProviderLocation): FormGroup {
        let form = this.formBuilder.group({
            [this.EXTRA_LOCATION_ID]: [!!extraLocation?.id ? extraLocation.id : ''],
            [this.EXTRA_LOCATION]: this.formBuilder.group({
                [this.EXTRA_LOCATION_ID]: [!!extraLocation?.location ? extraLocation.location.id : ''],
                [this.EXTRA_LOCATION_SEARCHING]: [''],
            }),
        })
        return form;
    }

    createExtraLocationsFormArray(provider?: Provider) {

        this.index = 0;
        let providerLocationsFormArray;

        if (!!provider?.providersLocations && provider.providersLocations.length != 0) {
            providerLocationsFormArray = provider.providersLocations.map((extraLocation) => {
                let extraLocationsForm;
                this.extraLocationsOnSelect[this.index] = [extraLocation.location];
                this.actualProviderExtraLocations[this.index] = extraLocation.location.id.toString();
                this.index++;

                if (!!extraLocation) {
                    extraLocationsForm = this.createLocation(extraLocation)
                } else {
                    extraLocationsForm = this.createLocation()
                }
                return extraLocationsForm;
            })
        } else {
            providerLocationsFormArray = [this.createLocation()];
        }
        return providerLocationsFormArray;
    }

    addProviderLocation(): void {
        this.providerLocationsFormArray.push(this.createLocation());
    }

    removeProviderLocation(index: number): void {
        this.providerLocationsFormArray.removeAt(index);
        this.extraLocationsOnSelect.splice(index, 1);
        this.actualProviderExtraLocations.splice(index, 1)
        // Force re-validation
        this.providerLocationsFormArray.markAsDirty();
        this.providerLocationsFormArray.updateValueAndValidity();
    }

    getMedicalLicensesTypeFormArray(i: number, j: number) {
        return this.getMedicalLicensesFormArray(i).at(j) as FormGroup;
    }

    getSpecialtiestFormGroup(index: number): FormGroup {
        return this.specialtiesFormArray.at(index) as FormGroup;
    }

    getAddressFormGroup(): FormGroup {
        // Debe devolver *exactamente* el FormGroup que contiene streetAddress, sin envolverlo de nuevo
        return this.formAddOrModifyProvider.get(this.ADDRESS) as FormGroup;
    }


    onConfirmWithoutAddress(checked: boolean) {
        this.providerWithoutNumberCheck = checked;

        const ctrl = this.formAddOrModifyProvider
            .get(this.ADDRESS)
            .get(this.ADDRESS_WITHOUT_STREET_NUMBER)!;

        // Solo parcheamos si cambió, y sin volver a emitir valueChanges
        if (ctrl.value !== checked) {
            ctrl.setValue(checked, { emitEvent: false });
        }
    }

    shortenFilename(filename) {
        const validExtensions = [".jpg", ".jpeg", ".png", ".pdf"];
        let variable1 = "";
        for (let ext of validExtensions) {
            if (filename?.endsWith(ext)) {
                variable1 = ext;
                break;
            }
        }
        if (!variable1) return filename;
        let variable2 = filename.slice(0, -variable1.length);
        if (variable2.length > 15) {
            variable2 = variable2.slice(0, 15) + "...";
        }
        return variable2 + variable1;
    }

    addFile(
        event: Event,
        type: EntitiesFiles,
        specialtyIndex?: number,
        licenseIndex?: number
    ) {
        const inputElem = event.target as HTMLInputElement;
        const files = inputElem.files;
        if (!files || files.length === 0) return;

        const maxSizeInBytes = 10 * 1024 * 1024;

        const isArrayForm = type === EntitiesFiles.TITLE || type === EntitiesFiles.MEDICAL_LICENSE;

        for (const file of Array.from(files)) {
            if (file.size > maxSizeInBytes) {
                this.alertService.openError('El archivo excede el tamaño máximo permitido de 10 MB.');
                inputElem.value = '';
                inputElem.blur();
                return;
            }
        }

        let form;
        switch (type) {
            case EntitiesFiles.DOCUMENT_BACK:
                form = this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_BACK);
                break;
            case EntitiesFiles.DOCUMENT_FRONT:
                form = this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_FRONT);
                break;
            case EntitiesFiles.PROFILE_PHOTO:
                form = this.formAddOrModifyProvider.get(this.PROFILE_MEDIA);
                break;
            case EntitiesFiles.TITLE:
                form = this.formAddOrModifyProvider.get(this.PROVIDER_TITLE);
                const TITLE_MAX = 5;
                const titleFileLenght = files?.length ? files.length : 0;
                const titleFormLenght = form?.value?.length ? form.value.length : 0;
                if (titleFileLenght + titleFormLenght > TITLE_MAX) {
                    this.errorMsg(`Solo puede cargar hasta ${TITLE_MAX} archivos`);
                    return;
                }
                break;
            case EntitiesFiles.MEDICAL_LICENSE:
                form = this.getMedicalLicensesFormArray(specialtyIndex)
                    .at(licenseIndex)
                    .get(this.MEDICAL_LICENSE_MEDIA);

                const LICENSES_MAX = 3;
                const licenseFileLenght = files?.length ? files.length : 0;
                const licenseFormLenght = form?.value?.length ? form.value.length : 0;
                if (licenseFileLenght + licenseFormLenght > LICENSES_MAX) {
                    this.errorMsg(`Solo puede cargar hasta ${LICENSES_MAX} archivos`);
                    return;
                }
                break;
            default:
                return;
        }

        if (isArrayForm) {
            const existing: File[] = form.value || [];
            const readers = Array.from(files).map((file) =>
                new Promise<File>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const dataUrl = reader.result as string;
                        const base64String = dataUrl.split(',')[1];
                        const mimeType = file.type;
                        const data = `${mimeType};base64,${base64String}`;
                        resolve({
                            fileUrl: `data:${data}`,
                            fileTypeCode: type,
                            fileName: file.name,
                        });
                    };
                    reader.onerror = (err) => reject(err);
                    reader.readAsDataURL(file);
                })
            );

            Promise.all(readers)
                .then((newMedias) => {
                    form.setValue([...existing, ...newMedias]);
                    form.markAsDirty();
                })
                .catch((err) => {
                    console.error('Error al procesar archivos:', err);
                })
                .finally(() => {
                    inputElem.value = '';
                    inputElem.blur();
                });
        } else {
            const file = files[0];
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result as string;
                const base64String = dataUrl.split(',')[1];
                const mimeType = file.type;
                const data = `${mimeType};base64,${base64String}`;
                const media: File = {
                    fileUrl: `data:${data}`,
                    fileTypeCode: type,
                    fileName: file.name,
                };
                form.setValue(media);
                form.markAsDirty();
                inputElem.value = '';
                inputElem.blur();
            };
            reader.onerror = (error) => {
                console.error('Error al leer el archivo', error);
            };
            reader.readAsDataURL(file);
        }
    }

    onPressDeleteFile(
        event: MouseEvent,
        type: EntitiesFiles,
        fileIndex?: number,
        specialtyIndex?: number,
        licenseIndex?: number
    ): void {
        event.stopPropagation();
        event.preventDefault();

        const swalWithCustomizeButtons = Swal.mixin({
            customClass: {
                confirmButton: 'btnSwalConfirm',
                cancelButton: 'btnSwalCancel'
            },
            buttonsStyling: true
        });
        swalWithCustomizeButtons.fire({
            text: '¿Eliminar archivo adjunto?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'CONFIRMAR',
            cancelButtonText: 'CANCELAR',
            reverseButtons: true
        }).then((result) => {
            if (result.isConfirmed) {
                const deletionActions: { [key: string]: () => void } = {
                    [EntitiesFiles.MEDICAL_LICENSE]: () => {
                        if (specialtyIndex !== undefined && licenseIndex !== undefined) {
                            const licenseControl = this.getMedicalLicensesFormArray(specialtyIndex)
                                .at(licenseIndex)
                                .get(this.MEDICAL_LICENSE_MEDIA);
                            const currentFiles: File[] = licenseControl.value || [];
                            currentFiles.splice(fileIndex, 1);
                            licenseControl.setValue(currentFiles);
                        }
                    },
                    [EntitiesFiles.TITLE]: () => {
                        const titleControl = this.formAddOrModifyProvider.get(this.PROVIDER_TITLE);
                        const currentFiles: File[] = titleControl.value || [];
                        currentFiles.splice(fileIndex, 1);
                        titleControl.setValue(currentFiles);
                    },
                    [EntitiesFiles.DOCUMENT_FRONT]: () => {
                        this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_FRONT).setValue(null);
                    },
                    [EntitiesFiles.DOCUMENT_BACK]: () => {
                        this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_BACK).setValue(null);
                    },
                    [EntitiesFiles.PROFILE_PHOTO]: () => {
                        this.formAddOrModifyProvider.get(this.PROFILE_MEDIA).setValue(null);
                    }
                };

                // Ejecuta la acción correspondiente si existe una lógica para el tipo recibido
                if (deletionActions[type]) {
                    deletionActions[type]();
                }

                this.successMsg('Archivo multimedia eliminado con éxito');
            }
        });

    }

    // Alert ok
    successMsg = (message) => {
        this.swalWithCustomizeButtons.fire({
            text: message,
            icon: 'success',
            showConfirmButton: false,
            timer: 1500
        }).then(() => {
            return;
        });
    };

    // Alert error
    errorMsg = (message) => {
        this.swalWithCustomizeButtons.fire({
            text: message,
            icon: 'error',
            showConfirmButton: false,
            timer: 2000
        }).then(() => {
            return;
        });
    };

    previewMedia(event: Event, file: string) {
        event.stopPropagation();
        event.preventDefault();
        if (this.fileUtilitiesService.isPdf(file)) {
            this.fileUtilitiesService.downloadFile(file)
        } else if (this.fileUtilitiesService.isPhoto(file)) {
            this.openDialogImage(file, false)
        }
    }

    // Show mediaUrls
    downloadMedia(event: Event, data: File, type: EntitiesFiles) {
        event.stopPropagation();
        event.preventDefault();
        let getMediaUUID;
        if (type === EntitiesFiles.PROFILE_PHOTO || type === EntitiesFiles.DOCUMENT_FRONT || type === EntitiesFiles.DOCUMENT_BACK || type === EntitiesFiles.TITLE) {
            getMediaUUID = this.actualProvider.mediaUUID
        } else if (type === EntitiesFiles.MEDICAL_LICENSE) {
            getMediaUUID = data.mediaUUID
        }
        try {
            this.providersService.getProviderMedia(data.id, getMediaUUID).subscribe({
                next: (fileUrl: any) => {
                    if (this.fileUtilitiesService.isPdf(fileUrl.file)) {
                        this.fileUtilitiesService.downloadFile(fileUrl.file);
                    } else if (this.fileUtilitiesService.isPhoto(fileUrl.file)) {
                        this.openDialogImage(fileUrl.file);
                    } else {
                        console.warn('Formato no reconocido:', fileUrl.file);
                    }
                },
                error: (err) => {
                    console.error('Error al obtener el archivo:', err);
                },
            });
        } catch (subscriptionError) {
            console.log(`FileId: ${data.id} and mediaUUID: ${getMediaUUID}`);
            console.error('Error al suscribirse al servicio de medios:', subscriptionError);
        }
    }


    openDialogImage(_image, buttons?): void {
        const dialogRef = this.dialog.open(DialogImageDetailComponent, {
            // width: '50%',
            data: { image: _image, buttons },
        });
        dialogRef.afterClosed().subscribe((result) => {
        });
    }

    getProviderMedia() {
        this.actualProvider?.files?.forEach(file => {
            switch (file.fileTypeCode) {
                case this.entitiesFiles.PROFILE_PHOTO:
                    this.formAddOrModifyProvider.get(this.PROFILE_MEDIA).setValue(file);
                    break;
                case this.entitiesFiles.DOCUMENT_FRONT:
                    this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_FRONT).setValue(file);
                    break;
                case this.entitiesFiles.DOCUMENT_BACK:
                    this.formAddOrModifyProvider.get(this.DOCUMENT_MEDIA_BACK).setValue(file);
                    break;
                case this.entitiesFiles.TITLE:
                    const currentTitles = this.formAddOrModifyProvider.get(this.PROVIDER_TITLE).value || [];
                    this.formAddOrModifyProvider.get(this.PROVIDER_TITLE).setValue([...currentTitles, file]);
                    break;
                default:
                    break;
            }
        });
    }

    private touchAndValidateSpecialtyGroup(specialtyIndex: number) {
        const fg = this.getSpecialtiestFormGroup(specialtyIndex);
        // Esto vuelve a correr TODOS los validators (incluido el custom)
        fg.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }

    onChangeObservation(event) {
        this.formAddOrModifyProvider.get(this.PROVIDER_OBSERVATIONS).setValue(event.target.value)
    }

    readAddress(event: Event) {
        console.log("EVENT ", event);
    }



    goToProviderSchedule(): void {
        this._dialogRef.close();

        const providerId = this.actualProvider?.id ?? '';
        const urlTree = this.router.createUrlTree(['/app', 'gestion', 'agenda', providerId]);
        const relativeUrl = this.router.serializeUrl(urlTree);
        const base = this.document.baseURI || this.document.location.origin + '/';
        const absoluteUrl = new URL(relativeUrl, base).toString();

        window.open(absoluteUrl, '_blank', 'noopener');
    }



    onGetSisa(licenseValue?: string) {
        this.copyToClipboard(licenseValue.toString());
        const base = 'https://sisa.msal.gov.ar/sisa/#sisa';
        const url = base;

        const w = 900, h = 700;
        const y = window.top?.outerHeight
            ? window.top.outerHeight / 2 + window.top.screenY - h / 2
            : 100;
        const x = window.top?.outerWidth
            ? window.top.outerWidth / 2 + window.top.screenX - w / 2
            : 100;

        window.open(
            url,
            'sisa_popup',
            `popup=yes,toolbar=no,location=no,status=no,menubar=no,` +
            `scrollbars=yes,resizable=yes,width=${w},height=${h},top=${y},left=${x}`
        );
    }

    copyToClipboard(value) { //copy text from medical license
        navigator.clipboard.writeText(value)
            .then(() => {
                this.copyToClipboard(value);
                this.alertService.openInfo('Texto copiado al portapapeles');
            })
            .catch(err => {
                console.error('Error al copiar:', err);
            });
    }

    isEnabledToUploadFiles(mLicenseId: Number, value: string): boolean {
        const isLicenseDefault = mLicenseId === this.DEFAULT_LICENSE_ID;
        if (isLicenseDefault || (this.addingProvider && !value) || (!value || Number(value) === 0)) {
            return false;
        }
        return this.addingProvider || this.updatingProvider;
    }

    getSpecialtyProfessionalTypes(specialtyIndex: number): SpecialtyProfessionalType[] {
        const specialtyId = this.getSpecialtiestFormGroup(specialtyIndex).get(this.SPECIALTIES_ID)?.value;
        return this.specialtyProfessionalTypes.filter(spt => spt.specialtyId == specialtyId);
    }

    onChangeSpecialtyProfessionalType(selectedId: string, specialtyIndex: number): void {
        this.getSpecialtiestFormGroup(specialtyIndex).get(this.SPECIALTY_PROFESSIONAL_TYPE_ID)?.patchValue(selectedId);
        this.formAddOrModifyProvider.markAsDirty();
    }
}
